/**
 * When a running turn is slow enough to say so, and when Stop deserves a
 * second look (PLAN v2 phase 1, user-facing half).
 *
 * Both rules are client-side and derived from state the SPA already has — no
 * protocol change, no server round-trip, nothing written from a hook.
 *
 * Why these two numbers:
 *
 * - **45 s** is the plan's first-token deadline (D2). Past it, a normal call
 *   would long since have started streaming; the thread has not gone quiet,
 *   the model is slow. Saying so keeps the user from assuming the tab is dead.
 * - **20 s** is the confirmation threshold on Stop, because the opposite
 *   mistake also happened in production: a user pressed Stop on a 12-minute
 *   run believing it had died, and threw the answer away seconds before it
 *   arrived. Under 20 s a stop is almost always "I meant something else" and
 *   must stay instant.
 */

/** No first token for this long → tell the user it is slow, not stuck. */
export const SLOW_TURN_MS = 45_000

/** A run older than this asks before it is stopped. */
export const STOP_CONFIRM_MS = 20_000

export interface SlowTurnInput {
  /** Whether the turn is running at all. */
  live: boolean
  /** When this turn started (the assistant placeholder's createdAt). */
  startedAt: string | null | undefined
  /** The live agent state. */
  state: string
  /** Has the reply produced anything visible yet — text or a step? */
  producedOutput: boolean
  now?: number
}

/**
 * "Tar längre tid än vanligt": 45 s in, and either nothing has come back yet
 * or the model is still reasoning (reasoning deltas are tokens, but a long
 * reasoning phase is exactly the wait the hint is for).
 */
export function isSlowTurn({
  live,
  startedAt,
  state,
  producedOutput,
  now = Date.now(),
}: SlowTurnInput): boolean {
  if (!live) return false
  const elapsed = elapsedMs(startedAt, now)
  if (elapsed === undefined || elapsed < SLOW_TURN_MS) return false
  return state === 'reasoning' || !producedOutput
}

/**
 * When the run in progress started, for the Stop confirmation: the live
 * assistant placeholder's `createdAt`, which is **server** time and the same
 * source the 45 s hint reads.
 *
 * The tempting alternative — marking the moment this tab saw the status turn
 * active — is wrong in exactly the case the dialog exists for: a tab opened or
 * reloaded eleven minutes into a run never saw that transition, would think the
 * run was seconds old, and would stop it instantly. `agentStatus.updatedAt` is
 * no better: it is refreshed on every delta.
 *
 * Null while no run is in progress, and for the round-trip before the
 * placeholder lands — a run that young stops without asking, which is right.
 */
export function busySinceFor(
  status: { state: string; messageId?: string } | undefined,
  messages: ReadonlyArray<{ id: string; createdAt?: string }>,
  isActive: (state: string) => boolean
): string | null {
  if (!status || !status.messageId || !isActive(status.state)) return null
  return messages.find((m) => m.id === status.messageId)?.createdAt ?? null
}

/** Stop on a run this old asks first — the answer may be seconds away. */
export function shouldConfirmStop(busySince: string | null | undefined, now = Date.now()): boolean {
  const elapsed = elapsedMs(busySince, now)
  return elapsed !== undefined && elapsed >= STOP_CONFIRM_MS
}

function elapsedMs(iso: string | null | undefined, now: number): number | undefined {
  if (!iso) return undefined
  const started = Date.parse(iso)
  if (!Number.isFinite(started)) return undefined
  return Math.max(0, now - started)
}
